import { Component, OnInit, TemplateRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import {
  EntrepotCaisseOption,
  EntrepotFormPayload,
  EntrepotTableRow,
  LogicielCaisseEntrepotService,
} from 'app/services/logiciel-caisse/logiciel-caisse-entrepot.service';
import { LogicielCaisseCaisseService } from 'app/services/logiciel-caisse/logiciel-caisse-caisse.service';

@Component({
  selector: 'app-logiciel-caisse-entrepots',
  templateUrl: './logiciel-caisse-entrepots.component.html',
  styleUrls: ['./logiciel-caisse-entrepots.component.scss'],
})
export class LogicielCaisseEntrepotsComponent implements OnInit {
  rows: EntrepotTableRow[] = [];
  filteredRows: EntrepotTableRow[] = [];
  searchTerm = '';
  loading = false;
  saving = false;
  editId: string | null = null;
  form: UntypedFormGroup;

  caisseOptions: EntrepotCaisseOption[] = [];
  caisseIdsByEntrepot = new Map<number, string[]>();
  caisseLabelsByEntrepot = new Map<number, string[]>();

  associationRow: EntrepotTableRow | null = null;
  associationCaisseIds: number[] = [];
  associationSaving = false;
  creatingCaisse = false;

  constructor(
    private readonly entrepotService: LogicielCaisseEntrepotService,
    private readonly caisseService: LogicielCaisseCaisseService,
    private readonly modalService: NgbModal,
    private readonly fb: UntypedFormBuilder,
    private readonly toastr: ToastrService
  ) {
    this.form = this.fb.group({
      libelle: ['', Validators.required],
      ville: [''],
      pays: [''],
      code_entrepot: [''],
      is_reassort: [false],
      is_globale: [false],
    });
  }

  ngOnInit(): void {
    this.loadRows();
  }

  loadRows(): void {
    this.loading = true;
    forkJoin({
          items: this.entrepotService.getEntrepotsErpTableRows(),
          caisses: this.entrepotService.listStationCaissesForAssociation(),
        }).subscribe({
        next: ({ items, caisses }) => {
          this.rows = items;
          this.applySearch();
          this.caisseOptions = caisses;
          const idsMap = new Map<number, string[]>();
          const labelsMap = new Map<number, string[]>();
          for (const c of caisses) {
            const eid = Number(c.entrepot_id);
            if (!Number.isFinite(eid) || eid <= 0) {
              continue;
            }
            const idList = idsMap.get(eid) ?? [];
            idList.push(String(c.id));
            idsMap.set(eid, idList);

            const shortLabel =
              c.numero_de_caisse != null ? `Caisse n° ${c.numero_de_caisse}` : `Caisse #${c.id}`;
            const labelList = labelsMap.get(eid) ?? [];
            labelList.push(shortLabel);
            labelsMap.set(eid, labelList);
          }
          this.caisseIdsByEntrepot = idsMap;
          this.caisseLabelsByEntrepot = labelsMap;
          this.loading = false;
          this.refreshFeatherIcons();
        },
        error: (err: Error) => {
          this.toastr.error(err.message);
          this.loading = false;
        },
      });
  }

  private loadCaisseLinks(): void {
    this.entrepotService.listStationCaissesForAssociation().subscribe({
      next: (caisses) => {
        this.caisseOptions = caisses;
        const idsMap = new Map<number, string[]>();
        const labelsMap = new Map<number, string[]>();
        for (const c of caisses) {
          const eid = Number(c.entrepot_id);
          if (!Number.isFinite(eid) || eid <= 0) {
            continue;
          }
          const idList = idsMap.get(eid) ?? [];
          idList.push(String(c.id));
          idsMap.set(eid, idList);

          const shortLabel =
            c.numero_de_caisse != null ? `Caisse n° ${c.numero_de_caisse}` : `Caisse #${c.id}`;
          const labelList = labelsMap.get(eid) ?? [];
          labelList.push(shortLabel);
          labelsMap.set(eid, labelList);
        }
        this.caisseIdsByEntrepot = idsMap;
        this.caisseLabelsByEntrepot = labelsMap;
        this.refreshFeatherIcons();
      },
      error: (err: Error) => this.toastr.error(err.message),
    });
  }

  caissesSummary(row: EntrepotTableRow): string {
    const eid = Number(row.id);
    const labels = this.caisseLabelsByEntrepot.get(eid);
    if (!labels?.length) {
      return '—';
    }
    return labels.join(', ');
  }

  openCreate(modal?: TemplateRef<unknown>): void {
    this.editId = null;
    this.form.reset({ is_reassort: false, is_globale: false });
    if (modal) {
      this.modalService.open(modal, { size: 'lg', centered: true });
    }
  }

  openEdit(row: EntrepotTableRow, modal: TemplateRef<unknown>): void {
    this.editId = row.id;
    this.form.patchValue({
      libelle: row.LIBELLÉ || '',
      ville: row.VILLE || '',
      pays: row.PAYS || '',
      code_entrepot: row['CODE ENTREPÔT'] || '',
      is_reassort: !!row.is_reassort,
      is_globale: !!row.is_globale,
    });
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  openAssociation(row: EntrepotTableRow, modal: TemplateRef<unknown>): void {
    this.associationRow = row;
    const eid = Number(row.id);
    this.associationCaisseIds = (this.caisseIdsByEntrepot.get(eid) ?? [])
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0);
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  createCaisseForAssociation(): void {
    if (!this.associationRow) {
      return;
    }
    const entrepotId = Number(this.associationRow.id);
    if (!Number.isFinite(entrepotId) || entrepotId <= 0) {
      return;
    }
    this.creatingCaisse = true;
    this.caisseService.createCaisse({ entrepot_id: entrepotId }).subscribe({
      next: () => {
        this.creatingCaisse = false;
        this.toastr.success('Caisse créée.');
        this.entrepotService.listStationCaissesForAssociation().subscribe({
          next: (caisses) => {
            this.caisseOptions = caisses;
            const newOnes = caisses.filter((c) => Number(c.entrepot_id) === entrepotId);
            const newest = newOnes[newOnes.length - 1];
            if (newest && !this.associationCaisseIds.includes(Number(newest.id))) {
              this.associationCaisseIds = [...this.associationCaisseIds, Number(newest.id)];
            }
            this.loadCaisseLinks();
          },
          error: (err: Error) => this.toastr.error(err.message),
        });
      },
      error: (err: Error) => {
        this.creatingCaisse = false;
        this.toastr.error(err.message);
      },
    });
  }

  saveAssociation(modalRef: { close: () => void }): void {
    if (!this.associationRow) {
      return;
    }
    const entrepotId = Number(this.associationRow.id);
    if (!Number.isFinite(entrepotId) || entrepotId <= 0) {
      return;
    }
    this.associationSaving = true;
    const ids = this.associationCaisseIds.filter((n) => Number.isFinite(n) && n > 0);
    this.entrepotService.syncEntrepotCaisses(entrepotId, ids).subscribe({
      next: () => {
        this.associationSaving = false;
        modalRef.close();
        this.associationRow = null;
        this.toastr.success('Association enregistrée.', 'Entrepôt');
        this.loadCaisseLinks();
      },
      error: (err: Error) => {
        this.associationSaving = false;
        this.toastr.error(err.message);
      },
    });
  }

  rowCode(row: EntrepotTableRow): string {
    return row['CODE ENTREPÔT'] || '—';
  }

  rowLibelle(row: EntrepotTableRow): string {
    return row.LIBELLÉ || '—';
  }

  rowPriorite(row: EntrepotTableRow): string {
    return row['PRIORITÉ'] || '—';
  }

  applySearch(): void {
    const kw = this.searchTerm.trim().toLowerCase();
    if (!kw) {
      this.filteredRows = [...this.rows];
    } else {
      this.filteredRows = this.rows.filter((row) => {
        const blob = `${this.rowCode(row)} ${this.rowLibelle(row)} ${row.VILLE} ${row.PAYS} ${this.rowPriorite(row)} ${this.caissesSummary(row)}`.toLowerCase();
        return blob.includes(kw);
      });
    }
    this.refreshFeatherIcons();
  }

  save(modalRef: { close: () => void }): void {
    if (this.form.invalid) {
      return;
    }
    const body: EntrepotFormPayload = {
      libelle: this.form.value.libelle,
      ville: this.form.value.ville || '',
      pays: this.form.value.pays || '',
      code_entrepot: this.form.value.code_entrepot || null,
      is_reassort: !!this.form.value.is_reassort,
      is_globale: !!this.form.value.is_globale,
    };
    this.saving = true;
    if (this.editId) {
      this.entrepotService.updateEntrepot(this.editId, body).subscribe({
        next: () => {
          this.toastr.success('Entrepôt mis à jour');
          this.saving = false;
          modalRef.close();
          this.loadRows();
        },
        error: (err: Error) => {
          this.toastr.error(err.message);
          this.saving = false;
        },
      });
    } else {
      this.entrepotService.createEntrepot(body).subscribe({
        next: () => {
          this.toastr.success('Entrepôt créé');
          this.saving = false;
          modalRef.close();
          this.loadRows();
        },
        error: (err: Error) => {
          this.toastr.error(err.message);
          this.saving = false;
        },
      });
    }
  }

  private refreshFeatherIcons(): void {
    setTimeout(() => {
      if (typeof (window as unknown as { feather?: { replace: () => void } }).feather?.replace === 'function') {
        (window as unknown as { feather: { replace: () => void } }).feather.replace();
      }
    }, 0);
  }
}
